import React, {useState} from 'react'
import {Box, Link, Typography} from '@mui/material'
import {Link as RouterLink} from 'react-router-dom'
import {
  ContentBox,
  NavBox,
  PrimaryLinkButton,
  WrappedBox,
} from '../../styles/global'
import {v4 as uuidv4} from 'uuid'
import Breadcrumb, {
  BreadcrumbType,
} from '../../new-dashboard/components/Breadcrumb'
import {
  CalendarAlertBox,
  Divider,
  DivisionItem,
  DivisionsBox,
  LightText,
  Subtitle,
  Title,
  VwFlexBox,
  VwLeftBox,
  VwRightBox,
  WorkshopBox,
} from './styles'
import {Organization} from '../../redux/courses'
import {getDivisionDetails} from '../../constants/divisions'
import {useAuth} from '../../components/AuthProvider'
import {
  LoadingStatuses,
  selectWorkshopsStatus,
  Workshop,
} from '../../redux/workshop'
import {LessonPlan} from '../../redux/lessons'
import convertDuration from '../../utils/convertDuration'
import {formatDuration} from 'date-fns'
import {communityUrls} from '../../constants/community-urls'
import {CalendarEvent, google, ics, outlook, yahoo} from 'calendar-link'
import moment from 'moment-timezone'
import {useAppDispatch} from '../../redux/configureStore'
import {selectMember} from '../../redux/members'
import {
  DateBox,
  UpcomingVirtualWorkshopContainer,
} from '../../components/DashboardContent/VirtualWorkshop/styles'
import {formatWorkshopDate} from '../../utils/formatWorkshopDates'
import {colors} from '../../styles/theme'
import {Button} from '../../components/button'
import CalendarIcon from '../../images/icons/calendar.svg'
import WorkshopSlidesDialog from '../../components/WorkshopSlidesDialog'
import ConflictedWorkshopDialog from '../../components/ConflictedWorkshopDialog'
import VirtualWorkshopForm from '../../components/DashboardContent/VirtualWorkshopForm'
import VirtualWorkshopDemoModal from '../../components/DashboardContent/VirtualWorkshopDemoModal'
import {useSelector} from 'react-redux'
import Loading from '../../components/Loading'
import Sidenav from '../../new-dashboard/components/Sidenav'
import CalendarAlert from '../../images/icons/calendar-alert-icon.svg'
import {handleUpgrade} from '../../utils/upgradeHelper'

type Props = {
  organization?: Organization
  activeCourse?: string
  onChangeActiveCourse: (value: string) => void
  workshops: Workshop[]
  lastWorkshop?: Workshop
  lesson?: LessonPlan
  hasAccess: boolean
  conflictedWorkshop?: Workshop
  isConflictDialogOpen: boolean
  onChangeIsConflictDialogOpen: (value: boolean) => void
  onRsvp: () => void
  onLaunchWorkshop: () => void
  isSubmitting: boolean
}

const View = ({
  organization,
  activeCourse,
  onChangeActiveCourse,
  workshops,
  lastWorkshop,
  conflictedWorkshop,
  lesson,
  hasAccess,
  isConflictDialogOpen,
  onChangeIsConflictDialogOpen,
  onRsvp,
  onLaunchWorkshop,
  isSubmitting,
}: Props) => {
  const auth = useAuth()
  const dispatch = useAppDispatch()

  const workshopStatus = useSelector(selectWorkshopsStatus)
  const member = useSelector(selectMember)

  const [open, setOpen] = useState(false)
  const [isSlidesDialogOpen, setIsSlidesDialogOpen] = useState(false)
  const [demoModalOpen, setDemoModalOpen] = useState(false)

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: 'Virtual Workshops',
      url: '#',
    },
  ]

  const courses = organization?.courses.filter(course => {
    return ['PcM', 'PjM', 'PA', 'PPD', 'PDD', 'CE'].includes(
      course.abbreviation,
    )
  })

  const currentCourse = courses?.find(c => c.id === activeCourse)

  const [hoverStates, setHoverStates] = useState<boolean[]>(
    courses?.map(() => false) || [],
  )

  const handleMouseEnter = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? true : hover)),
    )
  }

  const handleMouseLeave = (index: number) => {
    setHoverStates(
      prev => prev?.map((hover, i) => (i === index ? false : hover)),
    )
  }

  const formatPrereq = (prereq?: string) => {
    if (!prereq) return

    let title
    const preqReqParts = prereq.split('- ')

    if (preqReqParts.length === 3) {
      title = preqReqParts[0] + preqReqParts[1]
    } else {
      title = preqReqParts[0]
    }

    return title
  }
  const getNextWorkshopPrerequisitesApproximateDuration = (
    preReq: string[],
  ): string => {
    if (lesson) {
      const videoAssets = lesson.assets.filter(
        asset => asset.assetTypeAbbr === 'video',
      )

      const approximateDuration = preReq.reduce((acc, cur) => {
        const prereqId = cur?.split('video/')[1]?.trim()

        const asset = videoAssets.find(lesson => lesson.id === prereqId)

        if (asset) {
          return acc + convertDuration(asset.videoDuration!)
        }

        return acc
      }, 250) // "Getting Started with Virtual Workshops" duration

      return `approx. ${formatDuration(
        {
          hours: Math.floor(approximateDuration / 3600),
          minutes: Math.floor((approximateDuration % 3600) / 60),
          seconds: Math.floor(approximateDuration % 60),
        },
        {delimiter: '; '},
      )}`
    }

    return ''
  }

  const redirectToCommunity = () => {
    if (currentCourse) {
      const url = communityUrls[currentCourse.abbreviation]
      window.open(url, '_blank')
    }
  }

  const getEventData = (workshop?: Workshop) => {
    if (!workshop) return {} as CalendarEvent

    const startDate = new Date(workshop?.date)
    const endDate = new Date(workshop?.date)
    endDate.setHours(endDate.getHours() + 2)

    const centralStartDate = moment(startDate)
      .tz('America/Chicago')
      .format('YYYY-MM-DD HH:mm:ss ZZ')
    const centralEndDate = moment(endDate)
      .tz('America/Chicago')
      .format('YYYY-MM-DD HH:mm:ss ZZ')

    const response: CalendarEvent = {
      title: workshop?.title,
      description: workshop?.description,
      start: centralStartDate,
      end: centralEndDate,
      location: window.location.href,
    }

    return response
  }

  const formatPrereqURL = (url: string) => {
    if (url) {
      const preqReqParts = url.split('- ')
      if (preqReqParts.length === 3) {
        return preqReqParts[2]
      }

      return preqReqParts[1]
    }
  }

  const handleDownload = (workshop?: Workshop) => {
    const response = decodeURIComponent(
      ics(getEventData(workshop)).replace(
        /^data:text\/calendar;charset=utf8,/,
        '',
      ),
    )
    const blob = new Blob([response], {type: 'text/calendar;charset=utf-8'})
    const url = window.URL.createObjectURL(blob)
    return url
  }

  const handleDemoClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    setDemoModalOpen(true)
  }

  let nextWorkshopPrereq: string[] = []

  const nextWorkshop = workshops[0]
  const upcomingWorkshops = workshops.slice(1)

  if (nextWorkshop && auth.isAuthenticated && hasAccess) {
    const prereq = nextWorkshop?.prerequisites
    const idx = prereq?.indexOf('\n2.0')

    if (idx === -1) {
      nextWorkshopPrereq = [...prereq.split('\n')]
    } else {
      const version2Prereq = prereq?.slice(idx)
      const prereq2 = version2Prereq.split('\n2.0').slice(1)
      nextWorkshopPrereq = [...prereq2]
    }
  }

  return (
    <WrappedBox>
      <NavBox>
        <Sidenav />
      </NavBox>
      <ContentBox>
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <WorkshopBox>
          <Title variant="h1">Virtual Workshops</Title>
          <DivisionsBox>
            {courses?.map((course, key) => {
              const isActive = activeCourse === course.id

              const divisionDetails = getDivisionDetails(course.abbreviation)
              const icon =
                hoverStates[key] || isActive
                  ? divisionDetails?.hoverIcon
                  : divisionDetails?.icon

              return (
                <React.Fragment key={`course-${key}`}>
                  <DivisionItem
                    $isActive={isActive}
                    onMouseEnter={() => handleMouseEnter(key)}
                    onMouseLeave={() => handleMouseLeave(key)}
                    onClick={() => onChangeActiveCourse(course.id)}
                  >
                    <img src={icon} alt={course.title} /> {course.title}
                  </DivisionItem>
                </React.Fragment>
              )
            })}
          </DivisionsBox>
          <Divider />

          {activeCourse && (
            <>
              {workshopStatus === LoadingStatuses.Loading ||
              workshopStatus === LoadingStatuses.Idle ? (
                <Loading />
              ) : (
                <>
                  {lastWorkshop ? (
                    <>
                      <VwFlexBox>
                        <VwLeftBox>
                          <Subtitle variant="h3">
                            Replay the Latest Workshop:
                          </Subtitle>
                          <Typography variant="body1" marginTop={'8px'}>
                            {lastWorkshop.title}
                          </Typography>
                        </VwLeftBox>
                        <VwRightBox>
                          <PrimaryLinkButton
                            component={RouterLink}
                            to={`/workshops/replay/${lastWorkshop.workshopReplayId}`}
                          >
                            Watch
                          </PrimaryLinkButton>
                        </VwRightBox>
                      </VwFlexBox>
                      <Divider />
                    </>
                  ) : null}

                  <VwFlexBox>
                    <VwLeftBox>
                      <Subtitle variant="h3">Next virtual workshop</Subtitle>

                      {auth.isAuthenticated && hasAccess ? null : (
                        <>
                          <Typography variant={'body2'} marginTop={'16px'}>
                            <Link
                              href="#"
                              onClick={handleUpgrade(
                                dispatch,
                                auth,
                                member,
                                'virtual workshops paywall',
                              )}
                            >
                              Upgrade your subscription to an Expert plan
                            </Link>{' '}
                            to get access to this material.
                          </Typography>
                        </>
                      )}

                      <Typography
                        variant="body1"
                        marginTop={'16px'}
                        fontWeight={700}
                      >
                        {nextWorkshop.title}
                      </Typography>
                      {nextWorkshop.rsvp ? (
                        <LightText variant={'body2'} marginTop={'4px'}>
                          <span>✔ you RSVPed</span>
                        </LightText>
                      ) : null}
                      {nextWorkshop.displayAlert && (
                        <CalendarAlertBox>
                          <img src={CalendarAlert} alt="Date Changed" /> Date
                          Changed
                        </CalendarAlertBox>
                      )}
                      <Typography variant={'body2'} marginTop={'8px'}>
                        {formatWorkshopDate(nextWorkshop?.date)}
                      </Typography>
                      {auth.isAuthenticated && hasAccess ? (
                        <>
                          <Typography variant={'body2'} marginTop={'16px'}>
                            Add to Calendar:{' '}
                            <em>
                              <Link
                                href={`${handleDownload(nextWorkshop)}`}
                                target="_blank"
                              >
                                Apple
                              </Link>{' '}
                              |{' '}
                              <Link
                                href={`${google(
                                  getEventData(nextWorkshop),
                                )}&ctz=America/Chicago`}
                                target="blank"
                              >
                                Google Calendar
                              </Link>{' '}
                              |{' '}
                              <Link
                                href={outlook(getEventData(nextWorkshop))}
                                target="blank"
                              >
                                Outlook Live
                              </Link>{' '}
                              |{' '}
                              <Link
                                href={yahoo(getEventData(nextWorkshop))}
                                target="blank"
                              >
                                Yahoo
                              </Link>
                            </em>
                          </Typography>
                          <LightText variant={'body2'} marginTop={'16px'}>
                            {nextWorkshop?.description}
                          </LightText>
                          <Typography variant={'body2'} marginTop={'16px'}>
                            Prerequisites:
                            {nextWorkshopPrereq.length && lesson && (
                              <Typography
                                component={'span'}
                                variant="body2"
                                color={colors.bksGray60}
                              >
                                {' '}
                                (
                                {getNextWorkshopPrerequisitesApproximateDuration(
                                  nextWorkshopPrereq,
                                )}
                                )
                              </Typography>
                            )}
                          </Typography>

                          <Typography
                            variant={'body2'}
                            marginTop={'8px'}
                            key={uuidv4()}
                          >
                            <Link
                              href={'#'}
                              color={colors.blue1}
                              sx={{textDecoration: 'none'}}
                              onClick={handleDemoClick}
                            >
                              - Getting Started
                            </Link>
                          </Typography>

                          {nextWorkshopPrereq.map(prereq => (
                            <Typography
                              variant={'body2'}
                              marginTop={'8px'}
                              key={uuidv4()}
                            >
                              <Link
                                href={`${formatPrereqURL(prereq)}`}
                                color={colors.blue1}
                                sx={{textDecoration: 'none'}}
                              >
                                - {formatPrereq(prereq)}
                              </Link>
                            </Typography>
                          ))}
                        </>
                      ) : (
                        <>
                          <LightText variant={'body2'} marginTop={'16px'}>
                            {nextWorkshop?.description}
                          </LightText>
                        </>
                      )}
                    </VwLeftBox>
                    <VwRightBox>
                      <Button
                        disabled={
                          !auth.isAuthenticated ||
                          !hasAccess ||
                          !nextWorkshop ||
                          isSubmitting
                        }
                        color={!nextWorkshop?.rsvp ? 'secondary' : 'grey'}
                        children={!nextWorkshop?.rsvp ? 'RSVP' : 'UN-RSVP'}
                        onClick={onRsvp}
                      />
                      {nextWorkshop?.rsvp ? (
                        <Button
                          color={'primary'}
                          disabled={!nextWorkshop.isLaunchEnabled}
                          children={'Launch Workshop'}
                          onClick={onLaunchWorkshop}
                        />
                      ) : null}
                      <Button
                        color={'primary'}
                        disabled={
                          !auth.isAuthenticated || !hasAccess || !nextWorkshop
                        }
                        children={'Submit Question'}
                        onClick={() => setOpen(true)}
                      />
                      <Button
                        color={'primary'}
                        children={`${currentCourse?.abbreviation} Community`}
                        onClick={redirectToCommunity}
                      />
                      {nextWorkshop?.slidesLink ? (
                        <>
                          <Button
                            disabled={!auth.isAuthenticated || !hasAccess}
                            color={'primary'}
                            children={`Virtual Workshop Slides`}
                            onClick={() => setIsSlidesDialogOpen(true)}
                          />
                          <WorkshopSlidesDialog
                            open={isSlidesDialogOpen}
                            setOpen={setIsSlidesDialogOpen}
                            workshop={nextWorkshop}
                          />
                        </>
                      ) : null}
                    </VwRightBox>
                  </VwFlexBox>

                  {upcomingWorkshops.length > 0 ? (
                    <>
                      <Divider />
                      <UpcomingVirtualWorkshopContainer>
                        <Subtitle variant="h3" marginBottom={'16px'}>
                          Upcoming virtual workshops
                        </Subtitle>
                        <Box
                          display={'flex'}
                          flexDirection={'column'}
                          gap={'16px'}
                        >
                          {upcomingWorkshops.map(upcomingWorkshop => (
                            <Box key={uuidv4()}>
                              <Typography
                                variant={'body2'}
                                style={{fontWeight: 700, color: '#2d2d2d'}}
                                display={'flex'}
                                gap={'8px'}
                                alignItems={'center'}
                              >
                                {upcomingWorkshop.title}

                                {upcomingWorkshop.displayAlert && (
                                  <CalendarAlertBox marginTop={0}>
                                    <img
                                      src={CalendarAlert}
                                      alt="Date Changed"
                                    />{' '}
                                    Date Changed
                                  </CalendarAlertBox>
                                )}
                              </Typography>
                              <DateBox>
                                <LightText variant={'body2'}>
                                  {formatWorkshopDate(upcomingWorkshop.date)}
                                </LightText>
                                {auth.isAuthenticated && hasAccess && (
                                  <Link
                                    href={`${handleDownload(upcomingWorkshop)}`}
                                    target="_blank"
                                  >
                                    <img
                                      src={CalendarIcon}
                                      alt="Add to Calendar"
                                    />
                                  </Link>
                                )}
                              </DateBox>
                            </Box>
                          ))}
                        </Box>
                      </UpcomingVirtualWorkshopContainer>
                    </>
                  ) : null}
                </>
              )}
            </>
          )}

          <ConflictedWorkshopDialog
            open={isConflictDialogOpen}
            setOpen={onChangeIsConflictDialogOpen}
            workshop={nextWorkshop}
            conflictedWorkshop={conflictedWorkshop}
          />

          <VirtualWorkshopForm open={open} setOpen={setOpen} />

          <VirtualWorkshopDemoModal
            demoModalOpen={demoModalOpen}
            setDemoModalOpen={setDemoModalOpen}
          />
        </WorkshopBox>
      </ContentBox>
    </WrappedBox>
  )
}

export default View
